import { ImportFileType, IUnmanagedBusiness } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { convertLeadTimeToDays } from '@/coreDomainsModule/leadtime';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { PostUnmanagedBusinessInfo, UnmanagedCustomerForecast } from '@/coreInfrastructureModule/axios/generated/registerBusinessManage';
import { IRegisterBusinessRepository } from '@/coreInfrastructureModule/interfaces/IRegisterBusinessRepository';

/**
 *  未管理から取引を登録するユースケース
 */
export class RegisterBusinessFromUnmanagedBusinessUseCase {
  /**
   * 未管理から取引を登録する
   * @param repository - 未管理の取引の永続化の責務を負うクラス
   * @param unmanagedBusiness - 未管理の取引
   * @param importFileType - 取引情報のファイルタイプ
   * @param registerMasterInfoTypes - アップデートするマスタ情報
   * @param isUpsert - 既存の取引がある場合に更新するか
   */
  static async execute(
    repository: IRegisterBusinessRepository,
    unmanagedBusiness: IUnmanagedBusiness,
    importFileType: ImportFileType,
    registerMasterInfoTypes: string[] = [],
    isUpsert?: boolean
  ): Promise<INetworkResult<void, Error>> {
    try {
      // リードタイム単位と標準在庫指数をAPIに合わせる

      if (unmanagedBusiness.leadTimeUnit === undefined || unmanagedBusiness.leadTimeUnit === '')
        // NOTE: ↓リードタイムが不要な場合でも空文字で送るとAPI側のバリデーションに弾かれるため、daysを送信しておく
        unmanagedBusiness.leadTimeUnit = 'days';
      if (unmanagedBusiness.stdInventoryIndices) {
        // NOTE: ↓標準在庫指数が不要な場合でも空文字で送るとAPI側のバリデーションに弾かれるため、daysを送信しておく
        if (
          unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit === undefined ||
          unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit === ''
        )
          unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit = 'day';
        // NOTE: 標準在庫指数単位はdaysが弾かれるのでdayに変換
        if (unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit === 'days')
          unmanagedBusiness.stdInventoryIndices.標準在庫指数.option.unit = 'day';
      }

      // 未管理の取引から取引情報を作成
      const businessInfo: PostUnmanagedBusinessInfo = {
        warehouse: unmanagedBusiness.warehouse,
        warehouseNumber: unmanagedBusiness.warehouseNumber,
        package: Number(unmanagedBusiness.package),
        businessNumber: unmanagedBusiness.businessNumber,
        supplierName: unmanagedBusiness.supplierName,
        supplierNumber: unmanagedBusiness.supplierNumber,
        businessName: unmanagedBusiness.businessName ?? '',
        unit: unmanagedBusiness.unit ?? '',
        leadTime: convertLeadTimeToDays(Number(unmanagedBusiness.leadTime?.toString() ?? ''), unmanagedBusiness.leadTimeUnit),
        leadTimeUnit: unmanagedBusiness.leadTimeUnit,
        moq: unmanagedBusiness.moq,
        stdInventoryIndices: {
          標準在庫指数: {
            min: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.min ?? 0,
            max: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.max ?? 0,
            option: {
              unit: unmanagedBusiness.stdInventoryIndices?.標準在庫指数.option.unit,
            },
          },
        },
        arrivals: unmanagedBusiness.arrivals ?? [],
        inventories: unmanagedBusiness.inventories ?? [],
        executionDate: DateService.formatIDateToISOString(unmanagedBusiness.executionDate).toString(),
        customerName: unmanagedBusiness.customerName ?? '',
        customerNumber: unmanagedBusiness.customerNumber ?? '',
        salesDestinationName: unmanagedBusiness.salesDestinationName ?? '',
        salesDestinationNumber: unmanagedBusiness.salesDestinationNumber ?? '',
        salesResults: unmanagedBusiness.salesResults ?? [],
        customerAnnounces:
          unmanagedBusiness.announce?.map(
            (announce): UnmanagedCustomerForecast => ({
              year: announce.year,
              month: announce.month,
              manageInitiatedYearMonth: {
                year: announce.manageInitiatedYearMonth?.year ?? 0,
                month: announce.manageInitiatedYearMonth?.month ?? 0,
              },
              amount: announce.amount,
            })
          ) ?? [],
        aggregateBusinessId: unmanagedBusiness.aggregateBusinessId,
        baseBusinessId: unmanagedBusiness.baseBusinessId ?? '',
        endUser: unmanagedBusiness.endUser,
        manufacturer: unmanagedBusiness.manufacturer,
      };

      const convertBusinessTypeInfo = {
        [ImportFileType.Announce]: 'customerForecast',
        [ImportFileType.SalesResult]: 'salesResult',
        [ImportFileType.Inventory]: 'inventory',
        [ImportFileType.Arrival]: 'arrival',
      };

      const networkResult = await repository.postFromUnmanagedBusiness({
        registerBusinessInfo: businessInfo,
        registerBusinessInfoType: convertBusinessTypeInfo[importFileType],
        registerMasterInfoTypes: registerMasterInfoTypes, // FIXME: [未実装] 未管理取引から取引情報を作成する際にマスタ情報を登録する必要がある場合に指定する
      });

      if (networkResult.status.type === 'error') {
        return {
          status: {
            type: 'error',
            error: Error('取引情報の作成に失敗しました'),
          },
        };
      }

      return {
        status: {
          type: 'success',
          data: undefined,
        },
      };
    } catch (error) {
      console.error(error);
      return {
        status: {
          type: 'error',
          error: Error('想定外のエラーが発生しました'),
        },
      };
    }
  }
}
